import { CancelToken } from "axios";
import { AnyAction, Middleware } from "redux";
import { ThunkDispatch } from "redux-thunk";
import { StatusCode } from "api/protocols";
import { PutEmailCampaignDraft } from "api/rpc/2024-04/facilityAdmin/marketing/emailBuilder";
import type { IEmailCampaignDraft } from "api/rpc/2024-04/facilityAdmin/marketing/emailBuilderTypes";
import type { EmailDocument } from "pages/secure/facility/marketing/emailBuilder/domain/types";
import { ingestEmailDocumentFromApi } from "pages/secure/facility/marketing/emailBuilder/domain/ingest-api-document";
import { PutEmailMarketingTemplateDraft } from "api/rpc/2024-04/facilityAdmin/marketing/emailMarketingTemplates";
import { AuthActionTypes } from "redux/actions/actionTypes";
import {
  EmailBuilderActionType,
  isEmailBuilderAutosaveScheduleAction,
  type IEmailBuilderFlushAutosaveAction,
} from "redux/actions/emailBuilder/emailBuilderAction";
import { showError } from "redux/actions/ui";
import { shouldScheduleEmailBuilderAutosave, cloneEmailDocumentBaseline } from "pages/secure/facility/marketing/emailBuilder/emailBuilderAutosaveBaseline";
import { emailBuilderAutosaveDueAt } from "pages/secure/facility/marketing/emailBuilder/shell/emailBuilderDraftStatusLabel";
import { translateEmailBuilderValidationMessage } from "pages/secure/facility/marketing/emailBuilder/operations/translateEmailBuilderValidationMessage";
import {
  attemptEmailBuilderAutosave,
  EMAIL_BUILDER_AUTOSAVE_CONFLICT_MESSAGE,
  emailBuilderAutosaveErrorMessage,
  isEmailBuilderValidationApiMessage,
  type IEmailBuilderAutosaveApiResponse,
} from "redux/reducers/emailBuilder/emailBuilderAutosave";
import { EmailBuilderAutoSaver } from "redux/reducers/emailBuilder/emailBuilderAutoSaver";
import type { TEmailBuilderAutoSaveStatus } from "redux/reducers/emailBuilder/emailBuilder";
import { RootState } from "index";

const draftAutoSaver = new EmailBuilderAutoSaver();

function emailBuilderDraftPutApiMessage(response: {
  message?: string | null;
  data: unknown;
}): string | null {
  if (typeof response.message === "string" && response.message.trim().length > 0) {
    return response.message.trim();
  }

  if (typeof response.data === "string" && response.data.trim().length > 0) {
    return response.data.trim();
  }

  if (typeof response.data === "object" && response.data !== null) {
    const nested = response.data as { message?: unknown };
    if (typeof nested.message === "string" && nested.message.trim().length > 0) {
      return nested.message.trim();
    }
  }

  return null;
}

function dispatchAutosaveStatus(
  api: { dispatch: ThunkDispatch<RootState, unknown, AnyAction> },
  autoSaveStatus: TEmailBuilderAutoSaveStatus,
  autoSaveError: string | null = null,
  autoSaveConflict = false,
  autoSaveDueAt: number | null = null,
) {
  api.dispatch({
    type: EmailBuilderActionType.UPDATE_AUTOSAVE_STATUS,
    payload: {
      autoSaveStatus,
      autoSaveError,
      autoSaveConflict,
      autoSaveDueAt,
    },
  });
}

function createAutosaveHandlers(api: {
  dispatch: ThunkDispatch<RootState, unknown, AnyAction>;
  getState: () => RootState;
}) {
  let lastAutosaveErrorMessage: string | null = null;

  const updateStatus = (
    updatedStatus: TEmailBuilderAutoSaveStatus,
    autoSaveDueAt: number | null,
  ) => {
    const document = api.getState().emailBuilderStore.document;
    const rawErrorMessage =
      updatedStatus === "error"
        ? emailBuilderAutosaveErrorMessage(StatusCode.BAD_REQUEST, lastAutosaveErrorMessage, document)
        : null;
    const errorMessage =
      rawErrorMessage === null ? null : translateEmailBuilderValidationMessage(rawErrorMessage);
    dispatchAutosaveStatus(api, updatedStatus, errorMessage, false, autoSaveDueAt);
  };

  const save = async (token: CancelToken, saveGeneration: number): Promise<StatusCode> => {
    lastAutosaveErrorMessage = null;
    const state = api.getState().emailBuilderStore;

    if (
      state.workspace === null ||
      state.campaign === null ||
      state.draft === null ||
      state.document === null
    ) {
      return StatusCode.BAD_REQUEST;
    }

    const ownerId = state.workspace.id;
    const workspaceKind = state.workspace.kind;
    const schemaVersion = state.draft.schemaVersion;
    let revision = state.draft.revision;
    let document = state.document;
    let savedDocumentBaseline: EmailDocument | null = null;

    const putDraft = async (): Promise<IEmailBuilderAutosaveApiResponse<IEmailCampaignDraft>> => {
      const current = api.getState().emailBuilderStore;
      if (current.draft !== null) {
        revision = current.draft.revision;
      }
      if (current.document !== null) {
        document = current.document;
      }

      const putDocumentSnapshot = cloneEmailDocumentBaseline(document);

      if (workspaceKind === "template") {
        const response = await PutEmailMarketingTemplateDraft(
          ownerId,
          {
            revision,
            schema_version: schemaVersion,
            document,
          },
          false,
          token,
        );

        const apiMessage = emailBuilderDraftPutApiMessage(response);

        if (response.status !== StatusCode.OK || response.data == null) {
          return {
            status: response.status,
            message: apiMessage,
            data: {
              id: ownerId,
              campaign_id: ownerId,
              schema_version: schemaVersion,
              document,
              referenced_media: {},
              revision,
              created_at: null,
              updated_at: null,
            },
          };
        }

        const ingestedActiveDocument = ingestEmailDocumentFromApi(response.data.draft_document);
        savedDocumentBaseline = ingestedActiveDocument.ok
          ? cloneEmailDocumentBaseline(ingestedActiveDocument.document)
          : cloneEmailDocumentBaseline(putDocumentSnapshot);

        return {
          status: response.status,
          message: apiMessage,
          data: {
            id: response.data.id,
            campaign_id: response.data.id,
            schema_version: response.data.draft_schema_version,
            document: response.data.draft_document,
            referenced_media: response.data.referenced_media,
            revision: response.data.draft_revision,
            created_at: response.data.created_at,
            updated_at: response.data.updated_at,
          },
        };
      }

      const response = await PutEmailCampaignDraft(
        ownerId,
        {
          revision,
          schema_version: schemaVersion,
          document,
        },
        false,
        token,
      );

      if (response.status === StatusCode.OK && response.data?.document != null) {
        const ingestedSavedDocument = ingestEmailDocumentFromApi(response.data.document);
        savedDocumentBaseline = ingestedSavedDocument.ok
          ? cloneEmailDocumentBaseline(ingestedSavedDocument.document)
          : putDocumentSnapshot;
      } else if (response.status === StatusCode.OK) {
        savedDocumentBaseline = putDocumentSnapshot;
      }

      return {
        status: response.status,
        message: emailBuilderDraftPutApiMessage(response),
        data: response.data,
      };
    };

    const attempt = await attemptEmailBuilderAutosave({
      putDraft,
      cancelToken: token,
    });

    if (attempt.kind === "cancelled") {
      return StatusCode.BAD_REQUEST;
    }

    if (attempt.kind === "ok") {
      if (draftAutoSaver.isCurrentSaveGeneration(saveGeneration)) {
        const baselineDocument = savedDocumentBaseline ?? cloneEmailDocumentBaseline(document);
        api.dispatch({
          type: EmailBuilderActionType.AUTOSAVE_SUCCEEDED,
          payload: {
            revision: attempt.draft.revision,
            updatedAt: attempt.draft.updatedAt,
            baselineDocument,
          },
        });
      }
      return StatusCode.OK;
    }

    if (attempt.kind === "conflict") {
      return StatusCode.CONFLICT;
    }

    lastAutosaveErrorMessage = attempt.message;
    return attempt.status as StatusCode;
  };

  const dispatchError = (status: StatusCode) => {
    if (status === StatusCode.CONFLICT) {
      dispatchAutosaveStatus(
        api,
        "error",
        translateEmailBuilderValidationMessage(EMAIL_BUILDER_AUTOSAVE_CONFLICT_MESSAGE),
        true,
        null,
      );
      return;
    }

    const document = api.getState().emailBuilderStore.document;
    const rawMessage = emailBuilderAutosaveErrorMessage(status, lastAutosaveErrorMessage, document);
    const message = translateEmailBuilderValidationMessage(rawMessage);
    if (status === StatusCode.BAD_REQUEST && isEmailBuilderValidationApiMessage(lastAutosaveErrorMessage)) {
      dispatchAutosaveStatus(api, "unsaved-changes", null, false, emailBuilderAutosaveDueAt(Date.now()));
      return;
    }
    dispatchAutosaveStatus(api, "error", message, false, null);
    api.dispatch(showError(message));
  };

  return { updateStatus, save, dispatchError };
}

// eslint-disable-next-line @typescript-eslint/no-empty-object-type
export const emailBuilderMiddleware: Middleware<{}, RootState, ThunkDispatch<RootState, unknown, AnyAction>> = api => next => (action: AnyAction) => {
  const result = next(action);

  if (
    action.type === AuthActionTypes.USER_LOGIN ||
    action.type === AuthActionTypes.USER_LOGOUT ||
    action.type === EmailBuilderActionType.EXIT ||
    action.type === EmailBuilderActionType.START_LOADING ||
    action.type === EmailBuilderActionType.CANCEL_PENDING_AUTOSAVE
  ) {
    draftAutoSaver.cancelAutoSave();
  } else if (action.type === EmailBuilderActionType.FLUSH_AUTOSAVE) {
    const flushAction = action as IEmailBuilderFlushAutosaveAction;
    const { updateStatus, save, dispatchError } = createAutosaveHandlers(api);
    const saveResult = draftAutoSaver.saveNow(updateStatus, save, dispatchError);
    if (flushAction.meta?.onComplete !== undefined) {
      void saveResult.then(result => {
        flushAction.meta?.onComplete?.(result === "ok");
      });
    }
  } else if (isEmailBuilderAutosaveScheduleAction(action)) {
    const store = api.getState().emailBuilderStore;
    if (store.autoSaveConflict) {
      return result;
    }

    if (!shouldScheduleEmailBuilderAutosave(store)) {
      draftAutoSaver.cancelAutoSave();
      dispatchAutosaveStatus(api, "saved", null, false, null);
      return result;
    }

    const { updateStatus, save, dispatchError } = createAutosaveHandlers(api);
    draftAutoSaver.autoSave(updateStatus, save, dispatchError);
  }

  return result;
};
